import { useState } from 'react';

interface HeaderProps {
  scrolled: boolean;
}

export default function Header({ scrolled }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh-CN', name: '中文' },
    { code: 'ar', name: 'العربية' },
    { code: 'hi', name: 'हिन्दी' },
  ];

  const changeLanguage = (langCode: string) => {
    const googleTranslateElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
    if (googleTranslateElement) {
      googleTranslateElement.value = langCode;
      googleTranslateElement.dispatchEvent(new Event('change'));
    }
    setTranslateOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-gray-900 shadow-lg shadow-black/20' : 'bg-gray-900/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src="https://storage.readdy-site.link/project_files/21695a36-5064-423b-bac4-4ae2cbbe995a/7cfdc017-30df-42cc-821c-5d12781088e8_WhatsApp_Image_2026-03-23_at_9.17.45_AM-removebg-preview.png?v=3e6ee1f58e544bf931b71c33a97beab1"
              alt="JB GlobeX Logo"
              className="h-12 lg:h-14 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              Home
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              About
            </button>
            <button onClick={() => scrollToSection('products')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              Products
            </button>
            <button onClick={() => scrollToSection('quality')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              Quality
            </button>
            <button onClick={() => scrollToSection('certifications')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              Certifications
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              Testimonials
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-200 hover:text-teal-400 font-medium transition-colors whitespace-nowrap">
              Contact
            </button>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Google Translate Dropdown */}
            <div className="hidden lg:block relative">
              <button
                onClick={() => setTranslateOpen(!translateOpen)}
                className="flex items-center space-x-2 px-4 py-2 rounded-full border border-gray-600 text-gray-200 hover:border-teal-400 hover:text-teal-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-medium">Translate</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${translateOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {translateOpen && (
                <div className="absolute top-full right-0 mt-2 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-2 min-w-[160px]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-700 text-sm text-gray-200 whitespace-nowrap"
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/919743475615"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center justify-center w-10 h-10 bg-[#25D366] rounded-full hover:bg-[#20BA5A] transition-colors cursor-pointer"
            >
              <i className="ri-whatsapp-line text-white text-xl"></i>
            </a>

            {/* Contact Us Button */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden lg:block px-6 py-2.5 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-500 transition-all hover:shadow-lg whitespace-nowrap"
            >
              Contact Us
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-200 text-2xl"
            >
              <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-700">
            <nav className="flex flex-col space-y-3">
              <button onClick={() => scrollToSection('home')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                Home
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                About
              </button>
              <button onClick={() => scrollToSection('products')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                Products
              </button>
              <button onClick={() => scrollToSection('quality')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                Quality
              </button>
              <button onClick={() => scrollToSection('certifications')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                Certifications
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-200 hover:text-teal-400 font-medium text-left whitespace-nowrap">
                Contact
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-500 transition-colors text-center whitespace-nowrap"
              >
                Contact Us
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" className="hidden"></div>
    </header>
  );
}